import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import api from '../api/api';
import { toast } from 'sonner';
import { Sparkles, CheckCircle2, Circle, Clock, Target, TrendingUp, Calendar, AlertCircle, Bell, BookOpen, FileText, Brain } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

function Dashboard() {
  const authContext = useAuth();
  const user = authContext?.user;
  
  const [studyPlan, setStudyPlan] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dailyTopics, setDailyTopics] = useState([]);
  const [quizCount, setQuizCount] = useState(0);

  useEffect(() => {
    loadData();
    loadReminders();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const responses = await Promise.all([
        api.getTodayStudyPlan(headers),
        api.getProgressStats(headers)
      ]);

      setStudyPlan(responses[0].data);
      setStats(responses[1].data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReminders = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const headers = { Authorization: `Bearer ${token}` };
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      // Fetch daily topics and quiz questions
      const [topicsRes, quizRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/daily-topics`, { headers }),
        fetch(`${BACKEND_URL}/api/quiz-questions?completed=false`, { headers })
      ]);
      
      const topics = await topicsRes.json();
      const questions = await quizRes.json();
      
      setDailyTopics(topics || []);
      setQuizCount(questions?.length || 0);
    } catch (error) {
      console.error('Error loading reminders:', error);
    }
  };

  const handleGeneratePlan = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await api.generateStudyPlan(headers);
      setStudyPlan(response.data);
      toast.success('KI-Lernplan erfolgreich erstellt! 🎉');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Generieren des Lernplans');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleTask = async (taskIndex) => {
    if (!studyPlan) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const newCompleted = !studyPlan.tasks[taskIndex].completed;
      
      await api.completeStudyTask(studyPlan.id, taskIndex, newCompleted, headers);
      
      const updated = { ...studyPlan };
      updated.tasks[taskIndex].completed = newCompleted;
      setStudyPlan(updated);
      
      if (newCompleted) {
        toast.success('Aufgabe erledigt! +5 Punkte 🎯');
        // Reload stats to show updated points
        loadData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary-600"></div>
      </div>
    );
  }

  const completedTasks = studyPlan?.tasks?.filter(t => t.completed).length || 0;
  const totalTasks = studyPlan?.tasks?.length || 0;
  const progressPercent = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-heading font-bold text-slate-900 mb-2">
          Hallo, {user?.name || 'Schüler'}! 👋
        </h1>
        <p className="text-lg text-slate-600">Bereit für heute?</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-xl">
              <Target className="text-purple-600" size={24} />
            </div>
            <p className="text-slate-600 font-medium">Punkte</p>
          </div>
          <p className="text-3xl font-heading font-bold text-slate-900">{stats?.points || 0}</p>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-indigo-100 rounded-xl">
              <TrendingUp className="text-indigo-600" size={24} />
            </div>
            <p className="text-slate-600 font-medium">Fächer</p>
          </div>
          <p className="text-3xl font-heading font-bold text-slate-900">{stats?.total_subjects || 0}</p>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-teal-100 rounded-xl">
              <Calendar className="text-teal-600" size={24} />
            </div>
            <p className="text-slate-600 font-medium">Hausaufgaben</p>
          </div>
          <p className="text-3xl font-heading font-bold text-slate-900">
            {stats?.completed_homework || 0}/{stats?.total_homework || 0}
          </p>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-orange-100 rounded-xl">
              <AlertCircle className="text-orange-600" size={24} />
            </div>
            <p className="text-slate-600 font-medium">Klausuren</p>
          </div>
          <p className="text-3xl font-heading font-bold text-slate-900">{stats?.upcoming_exams || 0}</p>
        </div>
      </div>

      {/* Tägliche Erinnerungen */}
      <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-3xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="animate-bounce" size={28} />
          <h2 className="text-2xl font-heading font-bold">Deine täglichen Aufgaben</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Hausaufgaben Erinnerung */}
          <Link to="/homework" className="block">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/30 transition-all cursor-pointer">
              <div className="flex items-center gap-3">
                <FileText size={24} />
                <div>
                  <p className="font-bold">Hausaufgaben</p>
                  <p className="text-sm text-white/90">
                    {stats?.total_homework > stats?.completed_homework 
                      ? `${stats?.total_homework - stats?.completed_homework} noch offen!`
                      : 'Alles erledigt! ✓'}
                  </p>
                </div>
              </div>
            </div>
          </Link>
          
          {/* Themen Erinnerung */}
          <Link to="/lernhilfen" className="block">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/30 transition-all cursor-pointer">
              <div className="flex items-center gap-3">
                <BookOpen size={24} />
                <div>
                  <p className="font-bold">Heutige Themen</p>
                  <p className="text-sm text-white/90">
                    {dailyTopics.length === 0 
                      ? 'Trage deine Themen ein!'
                      : `${dailyTopics.length} Themen eingetragen`}
                  </p>
                </div>
              </div>
            </div>
          </Link>
          
          {/* Quiz Erinnerung */}
          <Link to="/lernhilfen" className="block">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/30 transition-all cursor-pointer">
              <div className="flex items-center gap-3">
                <Brain size={24} />
                <div>
                  <p className="font-bold">Wissenscheck</p>
                  <p className="text-sm text-white/90">
                    {quizCount > 0 
                      ? `${quizCount} Übungen warten!`
                      : 'Erstelle Übungen!'}
                  </p>
                </div>
              </div>
            </div>
          </Link>
        </div>
        
        {/* Motivations-Text */}
        <p className="mt-4 text-center text-white/90 text-sm">
          💪 Prüf, ob du das, was du heute gelernt hast, noch bei dir behalten hast!
        </p>
      </div>

      <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Sparkles className="text-primary-600" size={28} />
            <h2 className="text-2xl font-heading font-bold text-slate-900">Heutiger Lernplan</h2>
          </div>
          <Button
            onClick={handleGeneratePlan}
            disabled={generating}
            className="rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold px-6 shadow-lg hover:shadow-xl transition-all"
          >
            {generating ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Generiere...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Sparkles size={20} />
                <span>Plan generieren</span>
              </div>
            )}
          </Button>
        </div>

        {studyPlan && studyPlan.tasks && studyPlan.tasks.length > 0 ? (
          <div>
            <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-slate-700">
                  {completedTasks} von {totalTasks} Aufgaben erledigt
                </p>
                <p className="text-sm font-bold text-primary-600">{Math.round(progressPercent)}%</p>
              </div>
              <Progress value={progressPercent} className="h-3" />
            </div>

            <div className="space-y-4">
              {studyPlan.tasks.map((task, index) => (
                <div
                  key={index}
                  className={`p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300 hover:shadow-md ${
                    task.completed
                      ? 'bg-green-50 border-green-200'
                      : 'bg-slate-50 border-slate-200 hover:border-primary-300'
                  }`}
                  onClick={() => handleToggleTask(index)}
                >
                  <div className="flex items-start gap-4">
                    <button className="mt-0.5 flex-shrink-0">
                      {task.completed ? (
                        <CheckCircle2 className="text-green-600" size={28} strokeWidth={2.5} />
                      ) : (
                        <Circle className="text-slate-400 hover:text-primary-600 transition-colors" size={28} strokeWidth={2} />
                      )}
                    </button>
                    
                    <div className="flex-1">
                      <h3 className={`text-lg font-heading font-bold mb-1 ${
                        task.completed ? 'text-green-900 line-through' : 'text-slate-900'
                      }`}>
                        {task.title}
                      </h3>
                      <p className={`text-sm mb-2 ${task.completed ? 'text-green-700' : 'text-slate-600'}`}>
                        {task.description}
                      </p>
                      <div className="flex items-center gap-2 text-xs">
                        <Clock size={14} className={task.completed ? 'text-green-600' : 'text-slate-500'} />
                        <span className={task.completed ? 'text-green-700 font-medium' : 'text-slate-600 font-medium'}>
                          {task.duration} Minuten
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {completedTasks === totalTasks && totalTasks > 0 && (
              <div className="mt-6 p-6 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl text-white text-center">
                <div className="text-4xl mb-2">🎉</div>
                <h3 className="text-xl font-heading font-bold mb-1">Fantastisch!</h3>
                <p className="text-sm opacity-90">Du hast alle Aufgaben für heute erledigt!</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 mb-4">
              <Sparkles className="text-primary-600" size={40} />
            </div>
            <h3 className="text-xl font-heading font-bold text-slate-900 mb-2">
              Noch kein Lernplan für heute
            </h3>
            <p className="text-slate-600 mb-6">
              Klicke auf "Plan generieren" und die KI erstellt einen personalisierten Lernplan für dich!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              <div className="p-4 bg-indigo-50 rounded-xl">
                <p className="font-bold text-indigo-900 mb-1">📚 Intelligent</p>
                <p className="text-sm text-indigo-700">KI berücksichtigt deine Hausaufgaben und Klausuren</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl">
                <p className="font-bold text-purple-900 mb-1">⏱️ Strukturiert</p>
                <p className="text-sm text-purple-700">Ca. 1 Stunde Lernzeit pro Tag</p>
              </div>
              <div className="p-4 bg-teal-50 rounded-xl">
                <p className="font-bold text-teal-900 mb-1">🎯 Fokussiert</p>
                <p className="text-sm text-teal-700">Konkrete Aufgaben statt Überforderung</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl p-8 text-white">
        <h3 className="text-2xl font-heading font-bold mb-4">Willkommen bei Lernenachplan! 🎓</h3>
        <p className="mb-4 opacity-90">
          Nutze die Menüpunkte links, um Fächer, Hausaufgaben und Klausuren zu verwalten.
          Die KI hilft dir, einen optimalen Lernplan zu erstellen.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/20 transition-all">
            <p className="font-bold">📚 Fächer</p>
            <p className="text-sm opacity-90">Füge deine Schulfächer hinzu</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/20 transition-all">
            <p className="font-bold">✏️ Hausaufgaben</p>
            <p className="text-sm opacity-90">Tracke deine Aufgaben</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/20 transition-all">
            <p className="font-bold">📊 Fortschritt</p>
            <p className="text-sm opacity-90">Sieh deine Erfolge</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
